import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import {
    BleDevice,
    BluetoothLowEnergy,
    CharacteristicChangedEvent,
    DeviceScannedEvent
} from '@capgo/capacitor-bluetooth-low-energy';

import {
    normalizeNetworks,
    pinnableWifiChannel,
    ProvisioningCandidate,
    ProvisioningTransport,
    WifiJoinOptions,
    WifiNetwork
} from './deviceProvisioning';
import { wifiJoinErrorFromDeviceStatus } from './wifiProvisioningErrors';

export const ONLYCAT_PROVISIONING_SERVICE_UUID = '7b9c0000-3c1b-4f52-9e1a-1d5f0e9c1000';

const CHRC_SSID = '7b9c0001-3c1b-4f52-9e1a-1d5f0e9c1000';
const CHRC_PSK = '7b9c0002-3c1b-4f52-9e1a-1d5f0e9c1000';
const CHRC_STATUS = '7b9c0003-3c1b-4f52-9e1a-1d5f0e9c1000';
const CHRC_TRIGGER = '7b9c0004-3c1b-4f52-9e1a-1d5f0e9c1000';
const CHRC_SCAN_RESULT = '7b9c0005-3c1b-4f52-9e1a-1d5f0e9c1000';
const CHRC_DEVICE_INFO = '7b9c0006-3c1b-4f52-9e1a-1d5f0e9c1000';
const CHRC_PROVISIONING_ALLOWED = '7b9c0007-3c1b-4f52-9e1a-1d5f0e9c1000';

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();
const BLE_CONNECT_TIMEOUT_MS = 15000;
const BLE_DISCOVER_TIMEOUT_MS = 10000;
const BLE_READ_TIMEOUT_MS = 8000;
const BLE_WRITE_TIMEOUT_MS = 8000;
const BLE_NOTIFY_TIMEOUT_MS = 10000;
const BLE_SCAN_RECORD_TIMEOUT_MS = 45000;
const BLE_JOIN_STATUS_TIMEOUT_MS = 90000;

let initializePromise: Promise<void> | null = null;

interface DeviceInfo {
    deviceId?: string;
    firmwareVersion?: string;
}

type TriggerCommand = 'scan' | 'connect' | `connect:${number}` | 'disconnect' | 'forget';

type ScanRecord =
    | { t: 'start' }
    | { t: 'count'; n: number }
    | { t: 'ap'; s: string; r?: number; c?: number; a?: number; x?: number }
    | { t: 'done' }
    | { t: 'error'; e?: string };

export function candidateFromBleDevice(device: BleDevice): ProvisioningCandidate {
    return {
        mode: 'ble',
        id: device.deviceId,
        name: device.name || 'OnlyCat',
        rssi: device.rssi
    };
}

export async function ensureBleReady(): Promise<void> {
    initializePromise ??= BluetoothLowEnergy.initialize({ mode: 'central' }).catch(e => {
        initializePromise = null;
        throw e;
    });
    await initializePromise;

    const availability = await BluetoothLowEnergy.isAvailable();
    if (!availability.available) {
        throw new Error('ble_not_available');
    }

    const enabled = await BluetoothLowEnergy.isEnabled();
    if (!enabled.enabled) {
        throw new Error('ble_not_enabled');
    }

    const permissions = await BluetoothLowEnergy.requestPermissions();
    if (permissions.bluetooth === 'denied' || permissions.location === 'denied') {
        throw new Error('ble_permission_denied');
    }
}

export class BleProvisioningTransport implements ProvisioningTransport {
    readonly mode = 'ble' as const;
    readonly displayName: string;

    private connected = false;
    private info?: DeviceInfo;

    constructor(private readonly candidate: ProvisioningCandidate) {
        this.displayName = candidate.name;
        this.info = candidate.deviceId ? { deviceId: candidate.deviceId } : undefined;
    }

    get deviceId(): string | undefined {
        return this.info?.deviceId;
    }

    async prepare(signal?: AbortSignal): Promise<void> {
        await this.establishConnection(signal);

        if (!await this.readProvisioningAllowed()) {
            await this.cleanup();
            throw new Error('device_not_in_setup_mode');
        }
    }

    async waitForSetupMode(signal?: AbortSignal): Promise<void> {
        await this.establishConnection(signal);

        await this.withCharacteristicNotifications(CHRC_PROVISIONING_ALLOWED, signal, async nextValue => {
            // Read after subscribing so a state flip can't slip between the two.
            if (await this.readProvisioningAllowed()) {
                return;
            }

            while (true) {
                // User-paced — no timeout; the abort signal cancels the wait.
                const value = await nextValue();
                this.throwIfAborted(signal);

                // Values arrive text-decoded; the firmware notifies a single 0x01 byte.
                if (value.charCodeAt(0) === 1 || value === '1') {
                    return;
                }
            }
        });
    }

    private async establishConnection(signal?: AbortSignal): Promise<void> {
        if (this.connected) {
            return;
        }

        await ensureBleReady();
        this.throwIfAborted(signal);

        try {
            await withTimeout(
                BluetoothLowEnergy.connect({ deviceId: this.candidate.id }),
                BLE_CONNECT_TIMEOUT_MS,
                'ble_connection_timeout'
            );
        }
        catch (e: any) {
            if (e?.message === 'ble_connection_timeout' && await this.isConnected()) {
                console.warn('BLE connect timed out, but the device is now reported as connected; continuing.');
            }
            else if (e?.message === 'ble_connection_timeout') {
                throw e;
            }
            else {
                console.warn('BLE connect failed', e);
                throw new Error('ble_connection_failed');
            }
        }

        this.connected = true;
        this.throwIfAborted(signal);

        // Android keeps the default ATT MTU (23) unless the central negotiates,
        // and scan-result notifications are hard-capped at MTU - 3 bytes; iOS
        // and browser stacks negotiate on their own. Requesting 512 lands on
        // min(central, peripheral). Non-fatal: without it, setup degrades at
        // the scan step instead of failing to connect.
        if (Capacitor.getPlatform() === 'android') {
            try {
                const { mtu } = await withTimeout(
                    BluetoothLowEnergy.requestMtu({ deviceId: this.candidate.id, mtu: 512 }),
                    BLE_READ_TIMEOUT_MS,
                    'ble_mtu_timeout'
                );
                console.log('BLE MTU negotiated', mtu);
            }
            catch (e) {
                console.warn('BLE MTU negotiation failed; proceeding with default MTU', e);
            }
        }

        // Capgo's web shim enumerates descriptors for every characteristic during discovery.
        // WebBLE throws for valid characteristics with no descriptors; native still needs discovery.
        if (Capacitor.getPlatform() !== 'web') {
            try {
                await withTimeout(
                    BluetoothLowEnergy.discoverServices({ deviceId: this.candidate.id }),
                    BLE_DISCOVER_TIMEOUT_MS,
                    'ble_services_timeout'
                );
            }
            catch (e) {
                await this.cleanup();
                throw e;
            }
        }

        await this.readDeviceInfo();
    }

    async scanWifiNetworks(signal?: AbortSignal): Promise<WifiNetwork[]> {
        await this.ensureConnected(signal);

        const networks: WifiNetwork[] = [];

        await this.withCharacteristicNotifications(CHRC_SCAN_RESULT, signal, async nextRecord => {
            await this.writeTrigger('scan');

            while (true) {
                const record = await withTimeout(nextRecord(), BLE_SCAN_RECORD_TIMEOUT_MS, 'ble_wifi_scan_timeout');
                this.throwIfAborted(signal);

                const parsed = parseScanRecord(record);
                if (parsed === 'done') {
                    return;
                }

                if (parsed instanceof Error) {
                    throw parsed;
                }

                if (parsed) {
                    networks.push(parsed);
                }
            }
        });

        return normalizeNetworks(networks);
    }

    async joinWifiNetwork(ssid: string, password: string, options?: WifiJoinOptions): Promise<void> {
        const signal = options?.signal;
        const channel = pinnableWifiChannel(options?.channel);

        await this.ensureConnected(signal);

        await this.writeCharacteristic(CHRC_SSID, textEncoder.encode(ssid));
        await this.writeCharacteristic(CHRC_PSK, textEncoder.encode(password));

        await this.withCharacteristicNotifications(CHRC_STATUS, signal, async nextStatus => {
            await this.writeTrigger(channel ? `connect:${channel}` : 'connect');

            while (true) {
                const status = await withTimeout(nextStatus(), BLE_JOIN_STATUS_TIMEOUT_MS, 'ble_wifi_join_timeout');
                this.throwIfAborted(signal);

                if (status === 'connected') {
                    return;
                }

                if (status.startsWith('error:')) {
                    throw wifiJoinErrorFromDeviceStatus(status.substring('error:'.length));
                }
            }
        });
    }

    async cleanup(): Promise<void> {
        if (!this.connected) {
            return;
        }

        this.connected = false;

        try {
            await BluetoothLowEnergy.disconnect({ deviceId: this.candidate.id });
        }
        catch (e) {
            console.warn('Failed to disconnect BLE device', e);
        }
    }

    private async ensureConnected(signal?: AbortSignal): Promise<void> {
        if (!this.connected) {
            await this.prepare(signal);
        }

        this.throwIfAborted(signal);
    }

    private async readDeviceInfo(): Promise<void> {
        try {
            const { value } = await withTimeout(
                BluetoothLowEnergy.readCharacteristic({
                    deviceId: this.candidate.id,
                    service: pluginUuid(ONLYCAT_PROVISIONING_SERVICE_UUID),
                    characteristic: pluginUuid(CHRC_DEVICE_INFO)
                }),
                BLE_READ_TIMEOUT_MS,
                'ble_device_info_timeout'
            );
            const info = JSON.parse(textDecoder.decode(new Uint8Array(value))) as DeviceInfo;
            this.info = {
                ...this.info,
                ...info
            };
        }
        catch (e) {
            console.warn('OnlyCat BLE device info characteristic unavailable', e);
        }
    }

    private async readProvisioningAllowed(): Promise<boolean> {
        try {
            const { value } = await withTimeout(
                BluetoothLowEnergy.readCharacteristic({
                    deviceId: this.candidate.id,
                    service: pluginUuid(ONLYCAT_PROVISIONING_SERVICE_UUID),
                    characteristic: pluginUuid(CHRC_PROVISIONING_ALLOWED)
                }),
                BLE_READ_TIMEOUT_MS,
                'ble_provisioning_access_timeout'
            );
            const bytes = new Uint8Array(value);
            if (!bytes.length) {
                throw new Error('empty_provisioning_access');
            }
            return bytes[0] === 1;
        }
        catch (e: any) {
            if (e?.message === 'ble_provisioning_access_timeout') {
                throw e;
            }

            console.warn('OnlyCat BLE provisioning access characteristic unavailable', e);
            throw new Error('ble_provisioning_access_unavailable');
        }
    }

    private async writeTrigger(trigger: TriggerCommand): Promise<void> {
        await this.writeCharacteristic(CHRC_TRIGGER, textEncoder.encode(trigger));
    }

    private async writeCharacteristic(characteristic: string, value: Uint8Array): Promise<void> {
        await withTimeout(
            BluetoothLowEnergy.writeCharacteristic(this.writeOptions(characteristic, value)),
            BLE_WRITE_TIMEOUT_MS,
            'ble_write_timeout'
        );
    }

    private writeOptions(characteristic: string, value: Uint8Array): Parameters<typeof BluetoothLowEnergy.writeCharacteristic>[0] {
        return {
            deviceId: this.candidate.id,
            service: pluginUuid(ONLYCAT_PROVISIONING_SERVICE_UUID),
            characteristic: pluginUuid(characteristic),
            value: Array.from(value),
            type: 'withResponse'
        };
    }

    private async isConnected(): Promise<boolean> {
        try {
            const { devices } = await BluetoothLowEnergy.getConnectedDevices();
            return devices.some(device => device.deviceId === this.candidate.id);
        }
        catch {
            return false;
        }
    }

    private async withCharacteristicNotifications(
        characteristic: string,
        signal: AbortSignal | undefined,
        action: (nextValue: () => Promise<string>) => Promise<void>
    ): Promise<void> {
        const queue: string[] = [];
        let wake: ((value: string) => void) | undefined;
        let rejectWake: ((reason?: any) => void) | undefined;

        const listener = await BluetoothLowEnergy.addListener('characteristicChanged', (event: CharacteristicChangedEvent) => {
            if (event.deviceId !== this.candidate.id || !uuidEquals(event.service, ONLYCAT_PROVISIONING_SERVICE_UUID) || !uuidEquals(event.characteristic, characteristic)) {
                return;
            }

            const value = textDecoder.decode(new Uint8Array(event.value));
            if (wake) {
                const resolve = wake;
                wake = undefined;
                rejectWake = undefined;
                resolve(value);
            }
            else {
                queue.push(value);
            }
        });

        const abortHandler = () => {
            rejectWake?.(new DOMException('Provisioning cancelled', 'AbortError'));
        };
        signal?.addEventListener('abort', abortHandler, { once: true });

        try {
            await withTimeout(
                BluetoothLowEnergy.startCharacteristicNotifications({
                    deviceId: this.candidate.id,
                    service: pluginUuid(ONLYCAT_PROVISIONING_SERVICE_UUID),
                    characteristic: pluginUuid(characteristic)
                }),
                BLE_NOTIFY_TIMEOUT_MS,
                'ble_notification_timeout'
            );

            await action(() => {
                const value = queue.shift();
                if (value !== undefined) {
                    return Promise.resolve(value);
                }

                return new Promise<string>((resolve, reject) => {
                    wake = resolve;
                    rejectWake = reject;
                });
            });
        }
        finally {
            signal?.removeEventListener('abort', abortHandler);
            await removeListener(listener);

            try {
                await withTimeout(
                    BluetoothLowEnergy.stopCharacteristicNotifications({
                        deviceId: this.candidate.id,
                        service: pluginUuid(ONLYCAT_PROVISIONING_SERVICE_UUID),
                        characteristic: pluginUuid(characteristic)
                    }),
                    BLE_NOTIFY_TIMEOUT_MS,
                    'ble_notification_timeout'
                );
            }
            catch (e) {
                console.warn('Failed to stop BLE notifications', e);
            }
        }
    }

    private throwIfAborted(signal?: AbortSignal): void {
        if (signal?.aborted) {
            throw new DOMException('Provisioning cancelled', 'AbortError');
        }
    }
}

export async function scanBleProvisioningDevices(onDevice: (candidate: ProvisioningCandidate) => void, signal?: AbortSignal): Promise<void> {
    await ensureBleReady();
    if (signal?.aborted) {
        return;
    }

    const listener = await BluetoothLowEnergy.addListener('deviceScanned', (event: DeviceScannedEvent) => {
        if (event.device) {
            onDevice(candidateFromBleDevice(event.device));
        }
    });

    let stopped = false;
    let resolveStopped!: () => void;
    const stoppedPromise = new Promise<void>(resolve => {
        resolveStopped = resolve;
    });

    const stop = async () => {
        if (stopped) {
            return;
        }

        stopped = true;
        await removeListener(listener);
        try {
            await BluetoothLowEnergy.stopScan();
        }
        catch {
            // Scan may already have stopped after a timeout.
        }
        resolveStopped?.();
    };

    signal?.addEventListener('abort', stop, { once: true });

    try {
        if (signal?.aborted) {
            await stop();
            return;
        }

        await BluetoothLowEnergy.startScan({
            services: [pluginUuid(ONLYCAT_PROVISIONING_SERVICE_UUID)],
            allowDuplicates: true,
            timeout: 0
        });
        if (signal?.aborted || stopped) {
            await stop();
            return;
        }

        await stoppedPromise;
    }
    catch (e) {
        await stop();
        throw e;
    }
    finally {
        signal?.removeEventListener('abort', stop);
    }
}

function parseScanRecord(record: string): WifiNetwork | 'done' | Error | null {
    let parsed: ScanRecord;
    try {
        parsed = JSON.parse(record) as ScanRecord;
    }
    catch {
        // Most likely a record truncated by a too-small ATT MTU; surface a
        // translatable error instead of a raw SyntaxError.
        console.warn('Malformed BLE scan record', record);
        return new Error('ble_scan_record_malformed');
    }

    switch (parsed.t) {
    case 'done':
        return 'done';
    case 'error':
        return new Error(parsed.e ? `error:${parsed.e}` : 'error:scan');
    case 'ap':
        return {
            ssid: parsed.s,
            rssi: parsed.r,
            channel: parsed.c,
            auth: parsed.a,
            cipher: parsed.x
        };
    case 'start':
    case 'count':
        return null;
    default:
        return null;
    }
}

async function removeListener(listener: PluginListenerHandle): Promise<void> {
    await listener.remove();
}

function uuidEquals(a: string, b: string): boolean {
    return a.toLowerCase() === b.toLowerCase();
}

function pluginUuid(uuid: string): string {
    // Capgo iOS keys pending characteristic callbacks with the caller's UUID string,
    // then CoreBluetooth reports completion UUIDs uppercase.
    return Capacitor.getPlatform() === 'ios' ? uuid.toUpperCase() : uuid;
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number, message: string): Promise<T> {
    let timeout: ReturnType<typeof setTimeout> | undefined;

    const timeoutPromise = new Promise<never>((_, reject) => {
        timeout = setTimeout(() => reject(new Error(message)), timeoutMs);
    });

    return Promise.race([promise, timeoutPromise]).finally(() => {
        if (timeout) {
            clearTimeout(timeout);
        }
    });
}
